import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { IdCard, Plus, QrCode, Trash2, Download, AlertTriangle, CheckCircle2, ExternalLink, Copy } from "lucide-react";

/**
 * Admin panel for instructor credentials — captures data printed on the
 * physical badge and generates a scannable QR pointing at the public
 * verification page.
 */
export default function InstructorCredentialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [qrPreview, setQrPreview] = useState(null); // {credential, url}

  function emptyForm() {
    return {
      full_name: "",
      curp: "",
      puesto: "Instructor Cualificado",
      especialidad: "",
      issue_date: new Date().toISOString().slice(0, 10),
      valid_until: "",
    };
  }

  const load = async () => {
    try {
      const r = await api.get("/admin/instructor-credentials");
      setItems(r.data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = { ...form, curp: form.curp.trim().toUpperCase() };
      if (!payload.valid_until) delete payload.valid_until;
      if (!/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/.test(payload.curp)) {
        throw new Error("CURP inválido — debe tener 18 caracteres siguiendo el formato oficial");
      }
      const r = await api.post("/admin/instructor-credentials", payload);
      setFlash(`Credencial ${r.data.credential.credential_id} creada correctamente.`);
      setTimeout(() => setFlash(""), 3500);
      setForm(emptyForm());
      setShowForm(false);
      await load();
      // Auto-open QR preview
      openQr(r.data.credential);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const remove = async (id, label) => {
    if (!confirm(`¿Eliminar la credencial ${label}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/instructor-credentials/${id}`);
      setItems(items.filter((x) => x.id !== id));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openQr = async (cred) => {
    try {
      const r = await api.get(`/admin/instructor-credentials/${cred.id}/qr.png`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setQrPreview({ credential: cred, url });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const closeQr = () => {
    if (qrPreview?.url) URL.revokeObjectURL(qrPreview.url);
    setQrPreview(null);
  };

  const downloadQr = () => {
    if (!qrPreview) return;
    const a = document.createElement("a");
    a.href = qrPreview.url;
    a.download = `qr-${qrPreview.credential.credential_id}.png`;
    a.click();
  };

  const verifyUrl = (cred) => `${window.location.origin}/verify-instructor/${cred.credential_id}`;

  const copyLink = async (cred) => {
    try {
      await navigator.clipboard.writeText(verifyUrl(cred));
      setFlash("Enlace copiado al portapapeles");
      setTimeout(() => setFlash(""), 2500);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="instructor-credentials-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <IdCard className="w-6 h-6 text-naranja" />
            <h2 className="font-heading font-bold text-marino text-xl">Credenciales de Instructores</h2>
          </div>
          <p className="text-sm text-marino/70 max-w-3xl mt-1">
            Captura los datos que aparecen impresos en cada credencial física para generar un QR de verificación pública.
            El QR se imprime en la credencial; al escanearlo se abre <code>/verify-instructor/OMPEC-IC-######</code> mostrando
            nombre, CURP, puesto, especialidad, fecha de emisión y vigencia.
          </p>
        </div>
        <button
          data-testid="add-credential-btn"
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 bg-naranja text-white px-4 py-2 rounded-lg font-medium hover:bg-naranjaHover"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Cerrar formulario" : "Nueva credencial"}
        </button>
      </div>

      {flash && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 rounded-lg max-w-3xl">
          <CheckCircle2 className="w-4 h-4" /> {flash}
        </div>
      )}
      {err && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg max-w-3xl">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gris/30 rounded-xl p-6 shadow-sm max-w-3xl space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-marino/70 mb-1">Nombre completo del instructor</label>
              <input data-testid="cred-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full border border-gris rounded-lg px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-marino/70 mb-1">CURP</label>
              <input data-testid="cred-curp" value={form.curp} onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })} placeholder="JITJ910823HOCMLL03" maxLength={18} className="w-full border border-gris rounded-lg px-3 py-2 text-sm font-mono tracking-wider uppercase" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-marino/70 mb-1">Puesto</label>
              <input data-testid="cred-puesto" value={form.puesto} onChange={(e) => setForm({ ...form, puesto: e.target.value })} className="w-full border border-gris rounded-lg px-3 py-2 text-sm" required />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-marino/70 mb-1">Especialidad</label>
              <textarea data-testid="cred-especialidad" value={form.especialidad} onChange={(e) => setForm({ ...form, especialidad: e.target.value })} rows={2} className="w-full border border-gris rounded-lg px-3 py-2 text-sm resize-none" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-marino/70 mb-1">Fecha de emisión</label>
              <input data-testid="cred-issue" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full border border-gris rounded-lg px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-marino/70 mb-1">Vigencia <span className="text-marino/40">(opcional — por defecto +1 año)</span></label>
              <input data-testid="cred-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="w-full border border-gris rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-3">
            <button data-testid="cred-submit" disabled={busy} className="inline-flex items-center gap-2 bg-naranja text-white px-4 py-2 rounded-lg font-medium hover:bg-naranjaHover disabled:opacity-60">
              <QrCode className="w-4 h-4" /> {busy ? "Generando..." : "Crear credencial y QR"}
            </button>
            <button type="button" onClick={() => { setForm(emptyForm()); setShowForm(false); }} className="border border-gris text-marino px-4 py-2 rounded-lg font-medium hover:bg-marino/5">Cancelar</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gris/20 text-sm text-marino/70 font-medium">
          {items.length} credenciales registradas
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-marino/5">
              <tr>
                <th className="p-3 text-left">ID credencial</th>
                <th className="p-3 text-left">Instructor</th>
                <th className="p-3 text-left">CURP</th>
                <th className="p-3 text-left">Especialidad</th>
                <th className="p-3 text-left">Emisión</th>
                <th className="p-3 text-left">Vigencia</th>
                <th className="p-3 text-center w-52">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const expired = c.valid_until < new Date().toISOString().slice(0, 10);
                return (
                  <tr key={c.id} data-testid={`cred-row-${c.credential_id}`} className="border-b border-gris/20 hover:bg-marino/5">
                    <td className="p-3 font-mono text-xs text-marino">{c.credential_id}</td>
                    <td className="p-3 text-marino">{c.full_name}</td>
                    <td className="p-3 font-mono text-xs text-marino/70">{c.curp}</td>
                    <td className="p-3 text-marino/80 max-w-xs truncate" title={c.especialidad}>{c.especialidad}</td>
                    <td className="p-3 text-marino/80">{c.issue_date}</td>
                    <td className={`p-3 ${expired ? "text-red-600 font-semibold" : "text-marino/80"}`}>{c.valid_until}{expired ? " ⚠" : ""}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <button data-testid={`cred-qr-${c.credential_id}`} onClick={() => openQr(c)} title="Ver / descargar QR" className="p-1.5 text-marino hover:bg-naranja/10 hover:text-naranja rounded"><QrCode className="w-4 h-4" /></button>
                        <button data-testid={`cred-link-${c.credential_id}`} onClick={() => copyLink(c)} title="Copiar enlace público" className="p-1.5 text-marino hover:bg-marino/10 rounded"><Copy className="w-4 h-4" /></button>
                        <a href={verifyUrl(c)} target="_blank" rel="noreferrer" title="Abrir verificación" className="p-1.5 text-marino hover:bg-marino/10 rounded inline-flex"><ExternalLink className="w-4 h-4" /></a>
                        <button data-testid={`cred-del-${c.credential_id}`} onClick={() => remove(c.id, c.credential_id)} title="Eliminar" className="p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-marino/60">Aún no hay credenciales registradas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {qrPreview && (
        <div data-testid="qr-preview-modal" className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-marino text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-bold">
                <QrCode className="w-5 h-5 text-naranja" /> {qrPreview.credential.credential_id}
              </div>
              <button onClick={closeQr} className="text-white/70 hover:text-white">×</button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <img src={qrPreview.url} alt="QR" className="w-56 h-56 border border-gris/30 rounded-lg" />
              <div className="text-center">
                <div className="text-marino font-semibold">{qrPreview.credential.full_name}</div>
                <div className="text-xs text-marino/60 mt-1 break-all">{verifyUrl(qrPreview.credential)}</div>
              </div>
              <div className="flex gap-2 w-full">
                <button data-testid="qr-download" onClick={downloadQr} className="flex-1 bg-naranja text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Descargar PNG
                </button>
                <button onClick={closeQr} className="flex-1 border border-gris text-marino px-4 py-2 rounded-lg text-sm font-semibold hover:bg-marino/5">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
